import { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../App";
import { AiOutlineUser } from "react-icons/ai";
import {
  calculateDistance,
  getInlineLoader,
  roundToTwoDecimals,
} from "../Helpers";
import {
  STR_AVAILABLE_HOTELS,
  STR_AVAILABLE_ROOMS,
  STR_HOTEL_IS_FULL,
  STR_CHANGE_PHONE_NUMBER,
  STR_CONSULTATION_FEE_PAYMENTS,
  STR_CONTACT_US,
  STR_MENU_HOTELS,
  STR_NO_RECORD,
  STR_NOTIFICATIONS,
  STR_OPEN_MAPS,
  STR_PAYMENT_SETTINGS,
  STR_PERSONAL_SETTINGS,
  STR_ROOMS,
  STR_SUBSCRIPTION_FEE_PAYMENTS,
  STR_UPDATE_PERSONAL_INFO,
  STR_UPDATE_USERNAME,
  STR_USER_REVIEWS,
  STR_VIEW_OR_SEND_REVIEW,
} from "../Strings";
import { BsFillPersonLinesFill, BsPersonGear } from "react-icons/bs";
import {
  MdCheckCircle,
  MdContactPage,
  MdInfoOutline,
  MdOutlinePayment,
  MdOutlinePayments,
  MdOutlinePhonelinkSetup,
  MdOutlineSupport,
  MdSupportAgent,
  MdTranslate,
} from "react-icons/md";
import { CgProfile } from "react-icons/cg";
import OneHotelRoom from "../ones/OneHotelRoom";
import ImagesSlider from "../ones/ImagesSlider";
import { CDN_URL, callApi } from "../Helpers";
import { RiPinDistanceLine } from "react-icons/ri";
import { BiNavigation } from "react-icons/bi";
import MobileInterface from "../MobileInterface";
import { FaRegStar, FaStar } from "react-icons/fa";

export default function View({ id }) {
  const appContext = useContext(AppContext);

  /* Place states here */
  const [ready, setReady] = useState(false);

  const [data, setData] = useState(null);
  const [rooms, setRooms] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [images, setImages] = useState([]);
  const [distance, setDistance] = useState(null);
  const [rating, setRating] = useState(0);

  async function init() {
    //run initializations here
    setReady(false);
    await Promise.all([getData(), getRooms(), getReviews()]);
    setReady(true);
  }

  async function getData() {
    await callApi("get-hotel-data", { id }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data, check network");
      }
    });
  }

  async function getRooms() {
    await callApi("get-hotel-rooms", { hotelId: id }).then((response) => {
      if (response.status === 1) {
        setRooms(response.data);
      } else {
        appContext.tellError("Could not fetch data, check network");
      }
    });
  }

  const availableRoomsCount = (rooms || []).filter((room) => {
    return room.isAvailable === "yes" && room.isBooked === "no";
  }).length;

  const totalRoomsCount = (rooms || []).length;

  const hotelIsFull = totalRoomsCount > 0 && availableRoomsCount === 0;

  async function getReviews() {
    await callApi("get-hotel-reviews", { hotelId: id }).then((response) => {
      if (response.status === 1) {
        setReviews(response.data);
      } else {
        appContext.tellError("Could not fetch data, check network");
      }
    });
  }

  function calculateRating() {
    let _rating = 5;
    if (reviews && reviews.length > 0) {
      let _totalStars = 0;

      for (const _review of reviews) {
        _totalStars += Number(_review.numberOfStars);
      }

      _rating = Number(_totalStars / reviews.length).toFixed(1);
    } else {
      //leave default rating
    }

    setRating(_rating);
  }

  function calculateHotelDistance() {
    if (
      data &&
      appContext.currentLocation &&
      appContext.currentLocation.lat &&
      appContext.currentLocation.lng
    ) {
      const _distance = calculateDistance(
        appContext.currentLocation,
        JSON.parse(data.location),
      );

      setDistance(_distance);
    } else {
      setDistance(null);
    }
  }

  function openMaps() {
    const hotelLocation = JSON.parse(data.location);
    MobileInterface.openLink(
      `https://www.google.com/maps?q=${Number(hotelLocation.lat)},${Number(hotelLocation.lng)}`,
    );
  }

  function getImages() {
    if (data) {
      const _images = [];

      if (data.photo1) {
        _images.push(CDN_URL + data.photo1);
      }

      if (data.photo2) {
        _images.push(CDN_URL + data.photo2);
      }

      if (data.photo3) {
        _images.push(CDN_URL + data.photo3);
      }

      if (data.photo4) {
        _images.push(CDN_URL + data.photo4);
      }

      if (data.photo5) {
        _images.push(CDN_URL + data.photo5);
      }

      setImages(_images);
    }
  }

  useEffect(() => {
    calculateRating();
  }, [reviews]);

  useEffect(() => {
    getImages();
  }, [data]);

  useEffect(() => {
    calculateHotelDistance();
  }, [appContext.currentLocation, data]);

  useEffect(() => {
    init();
  }, []);

  if (ready) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <h6
              style={{
                fontSize: "32px",
              }}
              className="mt-4 mb-4 text-primary font-bold"
            >
              {data.name}
            </h6>

            <div className="card">
              <ImagesSlider images={images} />
              <div className="card-body">
                <pre className="mb-4">{data.description}</pre>

                <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
                  <div>
                    {hotelIsFull ? (
                      <span className="badge bg-dark">{STR_HOTEL_IS_FULL[appContext.language]}</span>
                    ) : (
                      <span className="badge bg-secondary">
                        {availableRoomsCount} {STR_AVAILABLE_ROOMS[appContext.language]}
                      </span>
                    )}
                  </div>
                  <div className="text-end">
                    {distance !== null ? (
                      <>
                        <span
                          style={{
                            fontSize: "24px",
                            display: "",
                          }}
                          className="font-bold badge bg-surface"
                        >
                          <RiPinDistanceLine size={32} />{" "}
                          {roundToTwoDecimals(distance)} Km
                        </span>
                        <br />
                      </>
                    ) : (
                      ""
                    )}

                    <button
                      className="btn bg-secondary btn-sm btn-rounded btn-padded"
                      onClick={openMaps}
                    >
                      <BiNavigation size={24} />{" "}
                      {STR_OPEN_MAPS[appContext.language]}
                    </button>
                  </div>
                </div>

                <div
                  className="text-center bg-light mt-4 mb-4"
                  style={{
                    padding: "10px",
                    borderRadius: "10px",
                  }}
                >
                  <div className="d-flex justify-content-center">
                    <FaStar
                      size={36}
                      className="align-self-center"
                      color="#B38B00"
                    />
                    <h6
                      style={{
                        fontSize: "36px",
                        color: "#B38B00",
                      }}
                      className="font-bold align-self-center mNoPadding mNoMargin"
                    >
                      {rating}
                    </h6>
                  </div>

                  <hr />

                  <h6
                    style={{
                      fontSize: "14px",
                    }}
                  >
                    {reviews && reviews.length > 0 ? reviews.length : ""}{" "}
                    {STR_USER_REVIEWS[appContext.language]}
                  </h6>

                  <button
                    className="btn btn-sm btn-rounded btn-dark"
                    onClick={() => {
                      appContext.navTo({
                        item: "side-view",
                        subItem: "create-hotel-review",
                        extraItem: id,
                      });
                    }}
                  >
                    {STR_VIEW_OR_SEND_REVIEW[appContext.language]}
                  </button>
                </div>

                {JSON.parse(data.features).map((item, i) => {
                  return (
                    <h6
                      style={{
                        fontSize: "18px",
                      }}
                      className="font-semi-bold"
                      key={i}
                    >
                      <MdCheckCircle className="text-primary" size={24} />
                      {appContext.getHotelAttributeName(item)}
                    </h6>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-4">
            <h6
              className="font-bold"
              style={{
                fontSize: "16px",
                textTransform: "uppercase",
              }}
            >
              {STR_ROOMS[appContext.language]}
            </h6>
          </div>

          {rooms && rooms.length > 0 ? (
            rooms.map((item, i) => {
              return (
                <div className="col-6 col-md-4" key={i}>
                  <OneHotelRoom key={i} hotelData={data} data={item} />
                </div>
              );
            })
          ) : (
            <h6>{STR_NO_RECORD[appContext.language]}</h6>
          )}
        </div>
      </div>
    );
  } else {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }
}
